'use client';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Music, Upload, Play, Pause, X, Trash2, Loader2, Check, ListMusic } from 'lucide-react';
import type { MusicChoice, MusicTrack } from '@/lib/types';

// The bundled World Cup default track. It's served from the app's public folder
// (like the flags) at a relative path; the render route resolves the leading
// slash to an absolute URL the Lambda can fetch.
export const WORLDCUP_DEFAULT_TRACK = {
  url: '/audio/worldcup-default.mp3',
  name: 'Reverse Side — Epic Cinematic (default)',
};

// A readable name for a stored track URL (rows only persist the URL). Falls back
// to the decoded filename when it isn't the known default.
export function nameForTrackUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url === WORLDCUP_DEFAULT_TRACK.url || url.endsWith(WORLDCUP_DEFAULT_TRACK.url)) return WORLDCUP_DEFAULT_TRACK.name;
  try {
    return decodeURIComponent(url.split('/').pop() || '') || 'Selected track';
  } catch {
    return 'Selected track';
  }
}

// Read an error response defensively (a proxy can return HTML, S3 returns XML),
// turning every failure into a readable message instead of "Unexpected token '<'".
async function errorMessage(res: Response, fallback: string): Promise<string> {
  const body = await res.text().catch(() => '');
  try {
    const json = JSON.parse(body);
    if (json?.error) return json.error;
  } catch { /* not JSON */ }
  if (res.status === 413) return `${fallback}: rejected as too large by the server (HTTP 413)`;
  return `${fallback} (HTTP ${res.status})`;
}

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Upload a track straight to S3 via a short-lived presigned PUT (bytes go
// browser → S3, bypassing the app server and the proxy body limit), then record
// it in the reusable music library. Content-addressed, so re-uploads dedupe.
export async function uploadAudioToS3(file: File): Promise<{ url: string; name: string }> {
  const contentType = file.type || 'audio/mpeg';
  const hash = await sha256Hex(file);

  const presignRes = await fetch('/api/music-library/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hash, contentType }),
  });
  if (!presignRes.ok) throw new Error(await errorMessage(presignRes, 'Could not start track upload'));
  const { uploadUrl, publicUrl, needsUpload } = (await presignRes.json()) as {
    uploadUrl?: string; publicUrl: string; needsUpload: boolean;
  };

  if (needsUpload && uploadUrl) {
    const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
    if (!put.ok) throw new Error(await errorMessage(put, 'Upload to storage failed'));
  }

  const reg = await fetch('/api/music-library', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ s3Url: publicUrl, name: file.name || null }),
  });
  if (!reg.ok) throw new Error(await errorMessage(reg, 'Failed to register track'));

  return { url: publicUrl, name: file.name || 'Uploaded track' };
}

interface TrackItem { url: string; name: string; id?: string; builtin?: boolean }

// Self-contained "Background Music" section: an on/off toggle plus a compact
// selected-track row. A "Choose Track" button opens a modal overlay where the
// user can upload a new track or pick a previously-used one. Controlled via
// `value` / `onChange`; usable both in the creation form and the row editor.
export default function MusicPicker({
  value, onChange, builtins = [],
}: {
  value: MusicChoice;
  onChange: (next: MusicChoice) => void;
  builtins?: { url: string; name: string }[];
}) {
  const [library, setLibrary] = useState<MusicTrack[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Single shared preview player; `playingUrl` marks which track is sounding.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  const loadLibrary = async () => {
    try {
      const res = await fetch('/api/music-library');
      const data = await res.json();
      setLibrary(data.tracks ?? []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadLibrary(); }, []);
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const stopPreview = () => { audioRef.current?.pause(); setPlayingUrl(null); };
  const closeOverlay = () => { stopPreview(); setOpen(false); };

  // Close the overlay on Escape (inline so the effect has no unstable deps).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      audioRef.current?.pause();
      setPlayingUrl(null);
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const togglePlay = (url: string) => {
    if (playingUrl === url) { audioRef.current?.pause(); setPlayingUrl(null); return; }
    audioRef.current?.pause();
    const a = new Audio(url);
    audioRef.current = a;
    a.onended = () => setPlayingUrl(null);
    a.play().then(() => setPlayingUrl(url)).catch(() => setPlayingUrl(null));
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const { url, name } = await uploadAudioToS3(file);
      await loadLibrary();
      onChange({ enabled: true, url, name }); // select the new track (overlay stays open to preview)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeFromLibrary = async (id: string, url: string) => {
    try { await fetch(`/api/music-library?id=${id}`, { method: 'DELETE' }); } catch (e) { console.error(e); }
    if (value.url === url) onChange({ ...value, url: null, name: null });
    if (playingUrl === url) stopPreview();
    setLibrary((prev) => prev.filter((t) => t.id !== id));
  };

  const selectTrack = (url: string, name: string) => {
    onChange({ enabled: true, url, name }); // picking a track implies "on"
    closeOverlay();
  };

  // Built-in tracks first, then library tracks (deduped by URL).
  const builtinItems: TrackItem[] = builtins.map((b) => ({ ...b, builtin: true }));
  const libraryItems: TrackItem[] = library
    .filter((t) => !builtins.some((b) => b.url === t.s3_url))
    .map((t) => ({ url: t.s3_url, name: t.name || 'Uploaded track', id: t.id }));
  const tracks = [...builtinItems, ...libraryItems];

  // Prefer a known catalog name for the selected track (the row editor only
  // persists the URL), falling back to whatever name the choice carries.
  const displayName = tracks.find((t) => t.url === value.url)?.name ?? value.name ?? 'Selected track';
  const enabled = value.enabled;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3 min-h-[28px]">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
          <Music size={13} className="text-violet-400" /> Background Music
        </h4>
        <button
          onClick={() => onChange({ ...value, enabled: !enabled })}
          role="switch"
          aria-checked={enabled}
          className={`inline-flex items-center gap-1.5 rounded-full pl-1 pr-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors border ${
            enabled ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' : 'bg-white/[0.03] text-slate-500 border-white/10 hover:text-slate-300'
          }`}
        >
          <span className={`w-4 h-4 rounded-full flex items-center justify-center ${enabled ? 'bg-emerald-500 text-black' : 'bg-white/10'}`}>
            {enabled && <Check size={10} strokeWidth={3} />}
          </span>
          {enabled ? 'On' : 'Off'}
        </button>
      </div>

      <div className={`space-y-2 transition-opacity ${enabled ? '' : 'opacity-40 pointer-events-none select-none'}`}>
        {value.url ? (
          <div className="flex items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/[0.06] px-3 py-2">
            <button
              onClick={() => togglePlay(value.url!)}
              className="shrink-0 w-7 h-7 rounded-full bg-violet-500/20 hover:bg-violet-500/40 text-violet-200 flex items-center justify-center transition-colors"
              title={playingUrl === value.url ? 'Pause' : 'Preview'}
            >
              {playingUrl === value.url ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
            </button>
            <span className="flex-1 min-w-0 truncate text-xs text-white">{displayName}</span>
            <button
              onClick={() => { if (playingUrl === value.url) stopPreview(); onChange({ ...value, url: null, name: null }); }}
              className="shrink-0 p-1 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              title="Remove track"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="text-slate-600 text-[11px] py-3 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">
            No track selected
          </div>
        )}

        <button
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] text-slate-300 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors"
        >
          <ListMusic size={13} /> {value.url ? 'Change Track' : 'Choose Track'}
        </button>
      </div>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in"
          onClick={closeOverlay}
        >
          <div
            className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10">
              <h3 className="text-[11px] font-black uppercase tracking-[0.15em] text-slate-300 flex items-center gap-2">
                <Music size={14} className="text-violet-400" /> Choose Background Track
              </h3>
              <button onClick={closeOverlay} title="Close" className="shrink-0 p-1.5 rounded text-slate-500 hover:text-white hover:bg-white/10 transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
              {/* Upload */}
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-white/10 rounded-lg py-4 cursor-pointer hover:border-white/30 hover:bg-white/[0.02] transition-all">
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => { handleUpload(e.target.files?.[0] ?? null); e.currentTarget.value = ''; }}
                />
                {uploading ? <Loader2 size={16} className="text-slate-500 animate-spin" /> : <Upload size={16} className="text-slate-500" />}
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                  {uploading ? 'Uploading…' : 'Upload a new track'}
                </span>
              </label>
              {error && <p className="text-[10px] text-red-400">{error}</p>}

              {/* Previously used / built-in tracks */}
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1.5">Previously used</p>
                {tracks.length === 0 ? (
                  <div className="text-slate-600 text-[11px] py-6 text-center border border-dashed border-white/10 rounded-lg font-mono uppercase tracking-widest">
                    No tracks yet — upload one above
                  </div>
                ) : (
                  <div className="space-y-1">
                    {tracks.map((t) => {
                      const selected = value.url === t.url;
                      return (
                        <div
                          key={t.url}
                          className={`flex items-center gap-2 rounded-lg border px-2 py-2 transition-colors ${
                            selected ? 'border-violet-500/40 bg-violet-500/[0.08]' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
                          }`}
                        >
                          <button
                            onClick={() => togglePlay(t.url)}
                            className="shrink-0 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-slate-200 flex items-center justify-center transition-colors"
                            title={playingUrl === t.url ? 'Pause' : 'Preview'}
                          >
                            {playingUrl === t.url ? <Pause size={12} fill="currentColor" /> : <Play size={12} fill="currentColor" />}
                          </button>
                          <button
                            onClick={() => selectTrack(t.url, t.name)}
                            className="flex-1 min-w-0 text-left flex items-center gap-2"
                            title="Use this track"
                          >
                            <span className="flex-1 min-w-0 truncate text-xs text-slate-200">{t.name}</span>
                            {t.builtin && <span className="shrink-0 text-[8px] font-black uppercase tracking-widest text-violet-400/80">Default</span>}
                            {selected && <Check size={13} className="shrink-0 text-violet-300" />}
                          </button>
                          {t.id && (
                            <button
                              onClick={() => removeFromLibrary(t.id!, t.url)}
                              className="shrink-0 p-1 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Delete from library"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10">
              <button onClick={closeOverlay} className="px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-white hover:bg-white/10 transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
